export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export function formatNumber(n: number): string {
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return Math.round(n).toLocaleString("en-US");
}

export function pct(n: number): string {
  return `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;
}

// Deterministic brand-ish color per company name — shared so a company's logo
// tile looks identical everywhere it appears (Profiles, Boost reply-as, …).
const COMPANY_COLORS = ["#2563eb", "#7c3aed", "#0891b2", "#059669", "#d97706", "#db2777", "#4f46e5", "#0d9488"];
export function companyColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return COMPANY_COLORS[h % COMPANY_COLORS.length];
}

/** "4 hours ago", localized by the browser — the channel post rows show
 *  when a post landed relative to now. Empty string for an unreadable date. */
export function timeAgo(input: string | number | Date | null | undefined, locale = "en"): string {
  const then = new Date(input ?? NaN).getTime();
  if (!Number.isFinite(then)) return "";
  const seconds = Math.round((then - Date.now()) / 1000);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 31536000], ["month", 2592000], ["week", 604800], ["day", 86400], ["hour", 3600], ["minute", 60],
  ];
  let rtf: Intl.RelativeTimeFormat;
  try { rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" }); } catch { rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" }); }
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  }
  return rtf.format(seconds, "second");
}

/** "500+", "1K+" … for figures that are estimates or only matter roughly.
 *  Small numbers stay exact. */
export function roughCount(n: number): string {
  const v = Math.max(0, Math.floor(Number(n) || 0));
  if (v < 10) return String(v);
  const steps = [1_000_000, 500_000, 100_000, 50_000, 10_000, 5_000, 1_000, 500, 100, 50, 10];
  const step = steps.find((s) => v >= s) ?? 10;
  return `${formatNumber(step)}+`;
}

/** Totals from the last-non-zero totals queries (getPostAnalyticsCount and
 *  friends): their three aliased aggregates, flattened to one sum object.
 *  A metric never read above 0 sums to null, returned as 0. */
export function lastNonZeroSums(data: any): {
  impression_count: number;
  like_count: number;
  comment_count: number;
} {
  const sum = (alias: string, col: string) =>
    Number(data?.[alias]?.aggregate?.sum?.[col]) || 0;
  return {
    impression_count: sum("impressions", "impression_count"),
    like_count: sum("likes", "like_count"),
    comment_count: sum("comments", "comment_count"),
  };
}
